// src/components/Tickets/TicketList.tsx - VERSIÓN FINAL CORREGIDA

import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useI18n } from '../../hooks/useI18n';
import { useAuthStore } from '../../stores/authStore';
import { ticketService } from '../../services/ticketService';
import { 
  Plus, Search, ChevronDown, Clock, Calendar, 
  AlertCircle, CheckCircle, User, TrendingUp, Ticket as TicketIcon,
  ChevronLeft, ChevronRight
} from 'lucide-react';
import { Ticket } from '../../types/ticket.types';

// Mapeo de estados
const mapStatusKey = (status: string): string => {
  const mapping: { [key: string]: string } = {
    'new': 'new', 'open': 'new', 'assigned': 'assigned',
    'in_progress': 'in_progress', 'waiting_user': 'waiting_customer',
    'waiting_customer': 'waiting_customer', 'resolved': 'resolved',
    'closed': 'closed'
  };
  return mapping[status] || 'new';
};

const mapPriorityKey = (priority: string): string => {
  const mapping: { [key: string]: string } = {
    'low': 'low', 'medium': 'medium', 'high': 'high', 'critical': 'critical'
  };
  return mapping[priority] || 'medium';
};

// Configuración con colores para bordes
const useStatusConfig = () => {
  const { t } = useI18n();
  return {
    new: { 
      label: t('tickets.status.new'), 
      bg: 'bg-blue-500/10', 
      text: 'text-blue-400',
      border: 'border-blue-500',
      icon: <Clock className="w-3.5 h-3.5" />
    },
    assigned: { 
      label: t('tickets.status.assigned'), 
      bg: 'bg-purple-500/10', 
      text: 'text-purple-400',
      border: 'border-purple-500',
      icon: <User className="w-3.5 h-3.5" />
    },
    in_progress: { 
      label: t('tickets.status.inProgress'), 
      bg: 'bg-amber-500/10', 
      text: 'text-amber-400',
      border: 'border-amber-500',
      icon: <TrendingUp className="w-3.5 h-3.5" />
    },
    waiting_customer: { 
      label: t('tickets.status.waitingCustomer'), 
      bg: 'bg-orange-500/10', 
      text: 'text-orange-400',
      border: 'border-orange-500',
      icon: <AlertCircle className="w-3.5 h-3.5" />
    },
    resolved: { 
      label: t('tickets.status.resolved'), 
      bg: 'bg-green-500/10', 
      text: 'text-green-400',
      border: 'border-green-500',
      icon: <CheckCircle className="w-3.5 h-3.5" />
    },
    closed: { 
      label: t('tickets.status.closed'), 
      bg: 'bg-gray-500/10', 
      text: 'text-gray-400',
      border: 'border-gray-500',
      icon: <CheckCircle className="w-3.5 h-3.5" />
    }
  };
};

const usePriorityConfig = () => {
  const { t } = useI18n();
  return {
    low: { label: t('tickets.priority.low'), badge: 'bg-gray-500/10 text-gray-400 border-gray-600' },
    medium: { label: t('tickets.priority.medium'), badge: 'bg-blue-500/10 text-blue-400 border-blue-600' },
    high: { label: t('tickets.priority.high'), badge: 'bg-orange-500/10 text-orange-400 border-orange-600' },
    critical: { label: t('tickets.priority.critical'), badge: 'bg-red-500/10 text-red-400 border-red-600' }
  };
};

export default function TicketList() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { t } = useI18n();
  const { user } = useAuthStore();
  
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  
  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const isAdminOrAgent = ['admin', 'agent', 'support', 'technician'].includes(user?.role || '');
  const STATUS_CONFIG = useStatusConfig();
  const PRIORITY_CONFIG = usePriorityConfig();

  const normalizeTicket = (ticket: any): Ticket => ({
    ...ticket,
    createdAt: ticket.created_at ? new Date(ticket.created_at) : new Date(),
    updatedAt: ticket.updated_at ? new Date(ticket.updated_at) : new Date(),
    lastActivity: ticket.updated_at ? new Date(ticket.updated_at) : new Date(),
    ticketNumber: ticket.id,
    subject: ticket.title,
    userName: ticket.user_name,
    userId: ticket.user_email
  });

const loadTickets = async () => {
  try {
    setLoading(true);
    console.log('🔍 [FRONTEND] Cargando tickets...');
    console.log('🔍 [FRONTEND] User role:', user?.role);
    console.log('🔍 [FRONTEND] isAdminOrAgent:', isAdminOrAgent);
    
    // ✅ TODOS LOS ROLES ADMINISTRATIVOS USAN EL MISMO ENDPOINT
    const data = isAdminOrAgent 
      ? await ticketService.getTickets()  // /api/tickets
      : await ticketService.getUserTickets(user!.email);  // /api/tickets/user/:email
    
    console.log('📥 [FRONTEND] Tickets recibidos:', data.tickets?.length);
    
    const normalized = (data.tickets || []).map(normalizeTicket);
    setTickets(normalized);
  } catch (error) {
    console.error('❌ [FRONTEND] Error cargando tickets:', error);
    setTickets([]);
  } finally {
    setLoading(false);
  }
};

useEffect(() => {
  if (user) {
    console.log('🔄 [FRONTEND] Usuario detectado, cargando tickets...');
    loadTickets();
  }
}, [user, isAdminOrAgent]);

  useEffect(() => {
    if (user) loadTickets();
  }, [user, isAdminOrAgent]);

  const stats = useMemo(() => {
    const openTickets = tickets.filter(t => ['new', 'assigned', 'in_progress'].includes(mapStatusKey(t.status)));
    const waitingReply = tickets.filter(t => mapStatusKey(t.status) === 'waiting_customer');
    const activeTickets = tickets.filter(t => mapStatusKey(t.status) === 'in_progress');
    const resolvedToday = tickets.filter(t => {
      const today = new Date();
      const ticketDate = new Date(t.updatedAt);
      return mapStatusKey(t.status) === 'resolved' && 
        ticketDate.toDateString() === today.toDateString();
    });

    return { open: openTickets.length, waiting: waitingReply.length, active: activeTickets.length, resolved: resolvedToday.length };
  }, [tickets]);

  const filteredTickets = useMemo(() => {
    return tickets.filter(ticket => {
      const matchesSearch = !searchTerm || 
        ticket.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.ticketNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.description?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = statusFilter === 'all' || mapStatusKey(ticket.status) === statusFilter;
      const matchesPriority = priorityFilter === 'all' || mapPriorityKey(ticket.priority) === priorityFilter;
      const matchesAssignee = assigneeFilter === 'all' || ticket.assignedTo === assigneeFilter;

      return matchesSearch && matchesStatus && matchesPriority && matchesAssignee;
    });
  }, [tickets, searchTerm, statusFilter, priorityFilter, assigneeFilter]);

  const paginatedTickets = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredTickets.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredTickets, currentPage]);

  const totalPages = Math.ceil(filteredTickets.length / itemsPerPage);

  const assignees = useMemo(() => {
    const unique = new Set(tickets.map(t => t.assignedTo).filter(Boolean));
    return Array.from(unique);
  }, [tickets]);

  const formatDate = (date: Date) => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return '—';
    
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return t('tickets.time.justNow') || 'Just now';
    if (minutes < 60) return `${minutes}m ${t('tickets.time.ago') || 'ago'}`;
    if (hours < 24) return `${hours}h ${t('tickets.time.ago') || 'ago'}`;
    if (days < 7) return `${days}d ${t('tickets.time.ago') || 'ago'}`;
    
    return new Intl.DateTimeFormat(t('tickets.locale') || 'es', { 
      day: '2-digit', 
      month: 'short' 
    }).format(date);
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'} p-6`}>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-1`}>
              {t('tickets.hello')} {user?.name?.split(' ')[0] || 'User'},
            </h3>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {t('tickets.subtitle')}
            </p>
          </div>
          
          <button
            onClick={() => navigate('/dashboard/tickets/create')}
            className="group relative px-5 py-2.5 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white rounded-lg font-medium transition-all duration-200 flex items-center gap-2 shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 hover:scale-105"
          >
            <Plus className="w-5 h-5 group-hover:rotate-90 transition-transform duration-200" />
            {t('tickets.newTicket')}
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-4">
          <div className={`${isDark ? 'bg-gradient-to-br from-gray-800 to-gray-850 border-gray-700/50' : 'bg-white border-gray-200'} rounded-xl p-6 border hover:border-blue-500/50 transition-all duration-200 group`}>
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                <AlertCircle className="w-6 h-6 text-blue-400" />
              </div>
            </div>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>{t('tickets.stats.open')}</p>
            <p className={`text-4xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{stats.open}</p>
          </div>

          <div className={`${isDark ? 'bg-gradient-to-br from-gray-800 to-gray-850 border-gray-700/50' : 'bg-white border-gray-200'} rounded-xl p-6 border hover:border-orange-500/50 transition-all duration-200 group`}>
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-orange-500/10 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                <Clock className="w-6 h-6 text-orange-400" />
              </div>
            </div>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>{t('tickets.stats.waiting')}</p>
            <p className={`text-4xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{stats.waiting}</p>
          </div>

          <div className={`${isDark ? 'bg-gradient-to-br from-gray-800 to-gray-850 border-gray-700/50' : 'bg-white border-gray-200'} rounded-xl p-6 border hover:border-amber-500/50 transition-all duration-200 group`}>
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-amber-500/10 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                <TrendingUp className="w-6 h-6 text-amber-400" />
              </div>
            </div>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>{t('tickets.stats.active')}</p>
            <p className={`text-4xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{stats.active}</p>
          </div>

          <div className={`${isDark ? 'bg-gradient-to-br from-gray-800 to-gray-850 border-gray-700/50' : 'bg-white border-gray-200'} rounded-xl p-6 border hover:border-green-500/50 transition-all duration-200 group`}>
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                <CheckCircle className="w-6 h-6 text-green-400" />
              </div>
            </div>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>{t('tickets.stats.resolved')}</p>
            <p className={`text-4xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{stats.resolved}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className={`${isDark ? 'bg-gray-800 border-gray-700/50' : 'bg-white border-gray-200'} rounded-xl p-4 mb-4 flex items-center gap-3 border`}>
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder={t('tickets.filters.search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`w-full pl-10 pr-4 py-2.5 ${isDark ? 'bg-gray-900 border-gray-700 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'} border rounded-lg text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
          />
        </div>

        <div className="relative">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={`appearance-none px-4 py-2.5 pr-10 ${isDark ? 'bg-gray-900 border-gray-700 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'} border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all cursor-pointer hover:border-gray-600`}
          >
            <option value="all">{t('tickets.filters.allStatus')}</option>
            <option value="new">{t('tickets.status.new')}</option>
            <option value="in_progress">{t('tickets.status.inProgress')}</option>
            <option value="resolved">{t('tickets.status.resolved')}</option>
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>

        <div className="relative">
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className={`appearance-none px-4 py-2.5 pr-10 ${isDark ? 'bg-gray-900 border-gray-700 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'} border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all cursor-pointer hover:border-gray-600`}
          >
            <option value="all">{t('tickets.filters.allPriority')}</option>
            <option value="critical">{t('tickets.priority.critical')}</option>
            <option value="high">{t('tickets.priority.high')}</option>
            <option value="medium">{t('tickets.priority.medium')}</option>
            <option value="low">{t('tickets.priority.low')}</option>
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>

       {isAdminOrAgent && (
              <div className="relative">
                <select
                  value={assigneeFilter}
                  onChange={(e) => setAssigneeFilter(e.target.value)}
                  className={`appearance-none px-4 py-2.5 pr-10 ${isDark ? 'bg-gray-900 border-gray-700 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'} border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all cursor-pointer hover:border-gray-600`}
                >
                  <option value="all">{t('tickets.filters.allAssignees')}</option>
                  {assignees.map(assignee => (
                    <option key={assignee} value={assignee}>{assignee}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            )}

        <button 
          onClick={() => {
            setStatusFilter('all');
            setPriorityFilter('all');
            setAssigneeFilter('all');
            setSearchTerm('');
          }}
          className={`px-4 py-2.5 text-sm text-blue-400 ${isDark ? 'hover:bg-blue-500/10' : 'hover:bg-blue-50'} rounded-lg transition-all border border-transparent hover:border-blue-500/30`}
        >
          {t('tickets.filters.resetFilters')}
        </button>
      </div>

      {/* Tabla con scroll */}
      <div className={`${isDark ? 'bg-gray-800 border-gray-700/50' : 'bg-white border-gray-200'} rounded-xl border overflow-hidden`}>
        <div className={`px-6 py-4 grid grid-cols-12 gap-4 text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-400 bg-gray-850 border-gray-700/50' : 'text-gray-600 bg-gray-50 border-gray-200'} border-b`}>
          <div className="col-span-4">{t('tickets.table.title')}</div>
          <div className="col-span-2">{t('tickets.table.status')}</div>
          <div className="col-span-2">{t('tickets.table.priority')}</div>
          <div className="col-span-2">{t('tickets.table.assignee')}</div>
          <div className="col-span-1">{t('tickets.table.updated')}</div>
          <div className="col-span-1">{t('tickets.table.created')}</div>
        </div>

       {/* Lista con scroll personalizado */}
<div className="max-h-[600px] overflow-y-auto" style={{
  scrollbarWidth: 'thin',
  scrollbarColor: isDark ? '#4B5563 transparent' : '#D1D5DB transparent'
}}>
  <style>{`
    .max-h-\\[600px\\]::-webkit-scrollbar {
      width: 6px;
    }
    .max-h-\\[600px\\]::-webkit-scrollbar-track {
      background: transparent;
    }
    .max-h-\\[600px\\]::-webkit-scrollbar-thumb {
      background-color: ${isDark ? '#4B5563' : '#D1D5DB'};
      border-radius: 3px;
    }
    .max-h-\\[600px\\]::-webkit-scrollbar-thumb:hover {
      background-color: ${isDark ? '#6B7280' : '#9CA3AF'};
    }
  `}</style>
  
  {paginatedTickets.length === 0 ? (
    <div className="p-12 text-center">
      <TicketIcon className={`w-16 h-16 ${isDark ? 'text-gray-600' : 'text-gray-400'} mx-auto mb-4`} />
      <p className={`text-lg font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
        {t('tickets.empty.title')}
      </p>
      <p className={isDark ? 'text-gray-500' : 'text-gray-600'}>
        {t('tickets.empty.description')}
      </p>
    </div>
  ) : (
    paginatedTickets.map((ticket, index) => {
      const statusConfig = STATUS_CONFIG[mapStatusKey(ticket.status)];
      const priorityConfig = PRIORITY_CONFIG[mapPriorityKey(ticket.priority)];

      if (!statusConfig || !priorityConfig) return null;

      // Colores hex para los bordes
      const borderColors: { [key: string]: string } = {
        'border-blue-500': '#3B82F6',
        'border-purple-500': '#A855F7',
        'border-amber-500': '#F59E0B',
        'border-orange-500': '#F97316',
        'border-green-500': '#10B981',
        'border-gray-500': '#6B7280'
      };

      return (
        <div
          key={ticket.id || index}
          onClick={() => navigate(`/dashboard/tickets/${ticket.id}`)}
          className={`
            px-6 py-5 grid grid-cols-12 gap-4 items-center 
            ${isDark ? 'hover:bg-gray-750 border-gray-700/30' : 'hover:bg-gray-50 border-gray-200'} 
            cursor-pointer transition-all duration-200 border-b last:border-0 
            group hover:scale-[1.02] hover:shadow-lg
            ${isDark ? 'hover:shadow-black/20' : 'hover:shadow-gray-300/50'}
          `}
          style={{
            borderLeft: `4px solid ${borderColors[statusConfig.border] || '#6B7280'}`
          }}
        >
          {/* Title */}
          <div className="col-span-4 flex items-center gap-3">
            <img
              src={`https://ui-avatars.com/api/?name=${encodeURIComponent(ticket.userName || 'User')}&background=4F46E5&color=fff&bold=true`}
              alt={ticket.userName}
              className={`w-10 h-10 rounded-lg ring-2 ${isDark ? 'ring-gray-700 group-hover:ring-blue-500' : 'ring-gray-200 group-hover:ring-blue-400'} transition-all duration-200 group-hover:scale-110`}
            />
            <div className="flex-1 min-w-0">
              <p className={`font-medium ${isDark ? 'text-white group-hover:text-blue-400' : 'text-gray-900 group-hover:text-blue-600'} truncate transition-colors`}>
                {ticket.subject}
              </p>
              <div className="flex items-center gap-2 mt-1">
                <span className={`text-xs font-mono ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>#{ticket.ticketNumber}</span>
                <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>•</span>
                <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{ticket.category || 'General'}</span>
              </div>
            </div>
          </div>

          {/* Status */}
          <div className="col-span-2">
            <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${statusConfig.bg} ${statusConfig.text} border border-current/20 group-hover:scale-105 transition-transform`}>
              {statusConfig.icon}
              {statusConfig.label}
            </div>
          </div>

          {/* Priority */}
          <div className="col-span-2">
            <span className={`inline-flex px-3 py-1.5 rounded-lg text-xs font-medium border ${priorityConfig.badge} group-hover:scale-105 transition-transform`}>
              {priorityConfig.label}
            </span>
          </div>

          {/* Assignee */}
          <div className="col-span-2 flex items-center gap-2">
            {ticket.assignedTo ? (
              <>
                <img
                  src={`https://ui-avatars.com/api/?name=${encodeURIComponent(ticket.assignedTo)}&background=random`}
                  alt={ticket.assignedTo}
                  className={`w-6 h-6 rounded-full ring-2 ${isDark ? 'ring-gray-700' : 'ring-gray-200'} group-hover:scale-110 transition-transform`}
                />
                <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'} truncate`}>
                  {ticket.assignedTo.split(' ')[0]}
                </span>
              </>
            ) : (
              <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{t('tickets.table.unassigned')}</span>
            )}
          </div>

          {/* Updated */}
          <div className={`col-span-1 flex items-center gap-1.5 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            <Clock className="w-3.5 h-3.5" />
            {formatDate(ticket.updatedAt)}
          </div>

          {/* Created */}
          <div className={`col-span-1 flex items-center gap-1.5 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            <Calendar className="w-3.5 h-3.5" />
            {formatDate(ticket.createdAt)}
          </div>
        </div>
      );
    })
  )}
</div>

        {/* Paginador */}
        {totalPages > 1 && (
          <div className={`px-6 py-4 flex items-center justify-between border-t ${isDark ? 'border-gray-700/50 bg-gray-850' : 'border-gray-200 bg-gray-50'}`}>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {t('tickets.pagination.showing') || 'Mostrando'} {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, filteredTickets.length)} {t('tickets.pagination.of') || 'de'} {filteredTickets.length}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className={`p-2 ${isDark ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'} rounded-lg transition-all disabled:opacity-30 disabled:cursor-not-allowed`}
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let page;
                if (totalPages <= 5) {
                  page = i + 1;
                } else if (currentPage <= 3) {
                  page = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  page = totalPages - 4 + i;
                } else {
                  page = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      currentPage === page
                        ? 'bg-blue-500 text-white'
                        : isDark
                          ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
              
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className={`p-2 ${isDark ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'} rounded-lg transition-all disabled:opacity-30 disabled:cursor-not-allowed`}
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}